import React, { useState, useEffect, useCallback } from 'react';
import { Table, Form, Button, Badge, Spinner, Alert } from 'react-bootstrap';
import api from '../../config/roundrobinApi';

export default function RulesTab({ active }) {
  const [agents, setAgents] = useState([]);
  const [queues, setQueues] = useState([]);
  const [rules, setRules] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  const fetchRules = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/rules');
      setAgents(res.data.agents);
      setQueues(res.data.queues);
      setRules(res.data.rules);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchRules(); }, [fetchRules]);

  useEffect(() => {
    if (active) fetchRules();
  }, [active, fetchRules]);

  const toggleRule = (agentZid, queueId) => {
    setRules(prev => ({
      ...prev,
      [agentZid]: {
        ...prev[agentZid],
        [String(queueId)]: !prev[agentZid]?.[String(queueId)]
      }
    }));
  };

  const handleSave = async () => {
    const rulesList = [];
    for (const agentZid of Object.keys(rules)) {
      for (const queueId of Object.keys(rules[agentZid])) {
        rulesList.push({
          agent_zendesk_id: agentZid,
          queue_id: parseInt(queueId),
          eligible: rules[agentZid][queueId]
        });
      }
    }
    setSaving(true);
    try {
      await api.put('/rules', { rules: rulesList });
      setMessage({ type: 'success', text: 'Rules saved' });
    } catch (err) { setMessage({ type: 'danger', text: 'Save failed' }); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="text-center py-5"><Spinner /></div>;

  return (
    <>
      {message && <Alert variant={message.type} dismissible onClose={() => setMessage(null)}>{message.text}</Alert>}
      <div className="d-flex justify-content-end mb-3">
        <Button variant="primary" onClick={handleSave} disabled={saving}>
          {saving ? <><Spinner size="sm" className="me-1" />Saving...</> : 'Save'}
        </Button>
      </div>

      {agents.length === 0 || queues.length === 0 ? (
        <Alert variant="info">Add active agents and queues first to configure rules.</Alert>
      ) : (
        <div className="rules-matrix">
          <Table bordered size="sm">
            <thead className="table-light">
              <tr>
                <th style={{ minWidth: '250px' }}>Agent</th>
                {queues.map(q => <th key={q.id} className="text-center" style={{ minWidth: '120px' }}>{q.name}</th>)}
              </tr>
            </thead>
            <tbody>
              {agents.map(agent => (
                <tr key={agent.zendesk_id}>
                  <td>
                    <div className="d-flex align-items-center">
                      <span className="me-2" style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: agent.active ? '#28a745' : '#ccc', display: 'inline-block' }} />
                      <div>
                        <strong>{agent.name}</strong>
                        <div>
                          {(agent.groups || []).map((g, i) => <Badge key={i} bg="info" className="me-1" style={{fontSize:'0.7em'}}>{typeof g === 'object' ? g.name : g}</Badge>)}
                          {(agent.tags || []).map((t, i) => <Badge key={i} bg="secondary" className="me-1" style={{fontSize:'0.7em'}}>{t}</Badge>)}
                        </div>
                      </div>
                    </div>
                  </td>
                  {queues.map(q => {
                    const eligible = rules[agent.zendesk_id]?.[String(q.id)] || false;
                    return (
                      <td key={q.id} className={`text-center ${eligible ? 'rule-cell-eligible' : 'rule-cell-ineligible'}`}>
                        <Form.Check type="switch" checked={eligible} onChange={() => toggleRule(agent.zendesk_id, q.id)} />
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}
    </>
  );
}
